/**
 * Compact inputs for the Mana Pricing scenario panel: the shared rate-sheet catalog fields
 * (same control as Other Lenders Pricing) and UWM's live layout fields (same controls as the
 * Pricing module).
 */
import clsx from 'clsx';
import { fieldOptionsFor, fieldValueList, type PricingField, type PricingFieldValue, type UwmFieldValue, type UwmLayoutField } from '@mana/shared';
import { MultiSelect } from '../../ui/MultiSelect.js';
import { SearchableSelect } from '../../ui/SearchableSelect.js';

export const fieldCls =
  'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200';
export const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500';

export function FieldInput({
  field,
  value,
  values,
  onChange,
}: {
  field: PricingField;
  value: PricingFieldValue | undefined;
  values: Record<string, PricingFieldValue>;
  onChange: (id: string, v: PricingFieldValue) => void;
}) {
  if (field.type === 'checkbox') {
    return (
      <label className="flex cursor-pointer items-center gap-2 self-end pb-1 text-xs text-slate-700">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(field.id, e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 accent-brand-navy-600" />
        {field.label}
      </label>
    );
  }
  const label = field.label + (field.required ? ' *' : '');
  if (field.type === 'multiselect') {
    const options = fieldOptionsFor(field, values);
    const picked = fieldValueList(value);
    const summary = picked.length === 0 ? '' : picked.length <= 3 ? picked.map((p) => p.replace(/\s*Years?$/i, ' yr')).join(', ') : `${picked.length} selected`;
    return (
      <div>
        <label className={labelCls} title={field.hint}>
          {label}
        </label>
        <div className="relative" title={picked.join(', ')}>
          <MultiSelect
            options={options.map((o) => ({ value: o }))}
            value={picked}
            onChange={(next) => onChange(field.id, options.filter((o) => next.includes(o)))}
            placeholder="Select…"
            popoverWidthClass="min-w-[13rem]"
          />
          {summary && (
            <span className="pointer-events-none absolute inset-y-0 left-3 right-14 flex items-center overflow-hidden bg-white text-sm text-slate-900">
              <span className="truncate">{summary}</span>
            </span>
          )}
        </div>
      </div>
    );
  }
  if (field.type === 'select') {
    const options = fieldOptionsFor(field, values);
    return (
      <div>
        <label className={labelCls} title={field.hint}>
          {label}
        </label>
        <select value={String(value ?? '')} onChange={(e) => onChange(field.id, e.target.value)} className={fieldCls}>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    );
  }
  const suffix = field.type === 'money' ? '$' : field.type === 'percent' ? '%' : null;
  return (
    <div>
      <label className={labelCls} title={field.hint}>
        {label}
      </label>
      <div className="relative">
        <input
          value={String(value ?? '')}
          inputMode={field.type === 'text' ? undefined : 'decimal'}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.id, e.target.value)}
          className={clsx(fieldCls, suffix && 'pr-6')}
        />
        {suffix && <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

const MULTI = 'Dropdown-MultiSelect';
const isNumberField = (t: string) => t.startsWith('Number-') || t === 'Calendar';

/** Drop options repeating a value (UWM's RefinancePurpose ships two). */
function dedupeByValue<T extends { value: string }>(opts: T[]): T[] {
  const seen = new Set<string>();
  return opts.filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)));
}

export function UwmFieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: UwmLayoutField;
  value: UwmFieldValue | undefined;
  error?: string;
  onChange: (itemId: string, v: UwmFieldValue) => void;
}) {
  const label = `${field.label}${field.isRequired || field.itemId === 'LoanTermIds' ? ' *' : ''}`;
  let control: React.ReactNode;
  if (field.displayInputType === 'Dropdown' && field.selectListData) {
    control = (
      <SearchableSelect
        options={dedupeByValue(field.selectListData.map((o) => ({ value: o.id, label: o.displayValue })))}
        value={typeof value === 'string' ? value : ''}
        onChange={(val) => onChange(field.itemId, val)}
      />
    );
  } else if (field.displayInputType === MULTI && field.selectListData) {
    control = (
      <MultiSelect
        options={field.selectListData.map((o) => ({ value: o.id, label: o.displayValue }))}
        value={Array.isArray(value) ? value : []}
        onChange={(next) => onChange(field.itemId, next)}
      />
    );
  } else {
    control = (
      <input
        type={isNumberField(field.displayInputType) ? 'number' : 'text'}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(field.itemId, e.target.value)}
        className={fieldCls}
      />
    );
  }
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {control}
      {error && <p className="mt-0.5 text-[10px] text-rose-600">{error}</p>}
    </div>
  );
}
